import type { NavigateFn } from "@reach/router"
import { Box, Button, ButtonLink, Flex, Navigation, OverflowBaseElementId } from "@shared/primitive-components"
import React, { PropsWithChildren } from "react"
import { BurgerIcon } from "@shared/primitive-components/src/Icons"
import BarLogo from "./BarLogo"
import ContentWrapper from "./styles/ContentWrapper"
import StyledBar from "./styles/StyledBar"
import { ReportAnIncident } from "../ReportAnIncident"
import * as styles from "./styles/screenStyles"

// --------------------------------------------------------------------------------------------------------------------

export const SCREEN_DTI = {
  LOGOUT_BUTTON: `logout_button`,
  HELP_BUTTON: `help_button`,
}

// --------------------------------------------------------------------------------------------------------------------

export type ScreenProps = Readonly<{
  additionalInfo?: JSX.Element | null
  showHelpButton?: boolean
  showClaimButton?: boolean
  homeButtonHandler?: React.MouseEventHandler<HTMLButtonElement>
  logoutButtonHandler: React.MouseEventHandler<HTMLButtonElement>
  navigate: NavigateFn
  navigationLinks: JSX.Element[]
  mobileNavIsOpen: boolean
  navTriggered: boolean
  setMobileNavIsOpen: (isOpen: boolean) => void
  setNavTriggered: (triggered: boolean) => void
  extraOptions?: JSX.Element | null
  hideNavigation: boolean
}>

// --------------------------------------------------------------------------------------------------------------------

export const Screen: React.FC<PropsWithChildren<ScreenProps>> = ({
  additionalInfo,
  children,
  showClaimButton,
  homeButtonHandler,
  logoutButtonHandler,
  navigate,
  navigationLinks,
  showHelpButton,
  mobileNavIsOpen,
  navTriggered,
  setMobileNavIsOpen,
  setNavTriggered,
  extraOptions,
  hideNavigation,
}) => (
  <>
    {!hideNavigation && (
      <StyledBar isOpen={mobileNavIsOpen}>
        <Flex flexDirection="column" alignItems="center" width="100%">
          <Flex css={styles.logoContainer} width="100%" alignItems="center" justifyContent="space-between">
            <BarLogo homeButtonHandler={homeButtonHandler} />
            <Button onClick={() => setMobileNavIsOpen(!mobileNavIsOpen)} css={styles.burgerButton}>
              <BurgerIcon />
            </Button>
          </Flex>
        </Flex>
        <Flex css={styles.navContainer} flexDirection="column" alignItems="center" justifyContent="space-between">
          <Navigation.Root navigateFn={navigate} navTriggered={navTriggered} setNavTriggered={setNavTriggered}>
            {navigationLinks}
          </Navigation.Root>
          <Flex css={styles.navOptionsContainer} flexDirection="column">
            <Box css={styles.buttonWrapper}>
              <ReportAnIncident hasPermission={showClaimButton} />
            </Box>

            {extraOptions}

            <Button
              variant="navigation"
              css={styles.logOutLink}
              onClick={logoutButtonHandler}
              data-testid={SCREEN_DTI.LOGOUT_BUTTON}
            >
              Log out
            </Button>
            {showHelpButton && (
              <ButtonLink
                href="https://help.flockcover.com/flock-for-brokers"
                target="_blank"
                variant="navigation"
                css={styles.link}
                data-testid={SCREEN_DTI.HELP_BUTTON}
              >
                Help
              </ButtonLink>
            )}
            {additionalInfo}
          </Flex>
        </Flex>
      </StyledBar>
    )}

    <ContentWrapper isOpen={mobileNavIsOpen} fullScreen={hideNavigation} id={OverflowBaseElementId}>
      {children}
    </ContentWrapper>
  </>
)
